/**
 * CORS-safe image loader for Canvas operations
 *
 * This utility ensures images from cross-origin sources (like R2/Cloudflare)
 * can be used in Canvas without CORS errors. It uses a persistent context
 * parameter to differentiate Canvas usage from regular <img> tags while
 * maintaining efficient caching.
 */

/**
 * Load an image with proper CORS headers for Canvas usage
 *
 * @param url - The image URL to load
 * @param context - Optional context identifier (defaults to 'canvas')
 * @returns Promise resolving to an HTMLImageElement ready for Canvas
 */
export async function loadImageForCanvas(
  url: string,
  context: string = 'canvas'
): Promise<HTMLImageElement> {
  try {
    // Add persistent context parameter to differentiate from regular img loads
    // This ensures R2 returns CORS headers while maintaining cache efficiency
    const separator = url.includes('?') ? '&' : '?';
    const corsUrl = `${url}${separator}context=${context}`;

    // Fetch with explicit CORS mode to ensure Origin header is sent
    const response = await fetch(corsUrl, {
      mode: 'cors',
      credentials: 'omit', // Don't send cookies for cross-origin requests
      headers: {
        // Ensure the request is treated as coming from our origin
        Accept: 'image/*',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to load image: ${response.status} ${response.statusText}`
      );
    }

    // Convert to blob to create a same-origin URL
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // Create and load the image
    const img = new Image();

    return new Promise((resolve, reject) => {
      img.onload = () => {
        // Clean up the object URL after loading
        // Note: We keep it alive as the image might be drawn multiple times
        // The browser will clean it up when the image is garbage collected
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to load image from blob URL`));
      };

      img.src = objectUrl;
    });
  } catch (error) {
    console.error('Error loading image for Canvas:', error);
    throw error;
  }
}

/**
 * Load an image with fallback to traditional loading if fetch fails
 * This provides backwards compatibility for local development or non-CORS scenarios
 *
 * @param url - The image URL to load
 * @param context - Optional context identifier
 * @returns Promise resolving to an HTMLImageElement
 */
export async function loadImageWithFallback(
  url: string,
  context: string = 'canvas'
): Promise<HTMLImageElement> {
  try {
    // Try CORS-safe loading first
    return await loadImageForCanvas(url, context);
  } catch (error) {
    console.warn(
      'CORS-safe loading failed, falling back to traditional loading:',
      error
    );

    // Fallback to traditional loading (might work for same-origin or permissive CORS)
    const img = new Image();
    img.crossOrigin = 'anonymous';

    return new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error('Failed to load image with fallback method'));
      img.src = url;
    });
  }
}

/**
 * Cleanup function to revoke object URLs created by loadImageForCanvas
 * Call this when you're done with the image to free memory
 *
 * @param img - The image element to cleanup
 */
export function cleanupCanvasImage(img: HTMLImageElement) {
  if (img.src && img.src.startsWith('blob:')) {
    URL.revokeObjectURL(img.src);
  }
}
